import { RefabricProURL } from "../index";
import { EnvelopeRead, readBare } from "api/envelope/readBare";
import { isEmailPreferences } from "./isEmailPreferences";
import {
  IActiveJobsResponse,
  IArchiveResponse,
  IMarkReadResponse,
  INotificationEmailPreferences,
  INotificationFeedRequest,
  INotificationFeedResponse,
  IUnreadCountResponse,
} from "./notifications.type";

// Generation-completion Notifications feed (REF-6306). All endpoints are requireLogin; like the
// What's New feed, these endpoints return BARE bodies (no `responseData` wrapper) — read the
// resolved value's fields directly (e.g. `res.items`), NOT `res.data.items`. The `as unknown as`
// cast bridges HttpClient's blanket `ApiResponse<T>` return type to the real body, exactly like
// whatsNew.service.ts.
export default {
  // Paginated notifications feed, sorted `last_event_at DESC, id DESC`, + the per-user unread count.
  async getFeed(params?: INotificationFeedRequest, config?: { signal?: AbortSignal }): Promise<INotificationFeedResponse> {
    return RefabricProURL.get("notifications", { params, ...config }) as unknown as Promise<INotificationFeedResponse>;
  },

  async getUnreadCount(config?: { signal?: AbortSignal }): Promise<IUnreadCountResponse> {
    return RefabricProURL.get("notifications/unread-count", { ...config }) as unknown as Promise<IUnreadCountResponse>;
  },

  // Marks notifications read. CRITICAL: `ids === undefined` sends NO body, which marks ALL unread
  // notifications read; `ids === []` sends `{ ids: [] }`, which marks NOTHING. These are opposite
  // outcomes — never coalesce an empty selection into "no ids".
  async markRead(ids?: string[]): Promise<IMarkReadResponse> {
    return RefabricProURL.patch("notifications/read", ids === undefined ? {} : { ids }, {}) as unknown as Promise<IMarkReadResponse>;
  },

  // Same "undefined = all, [] = none" body rule as markRead — see the comment above.
  async markSeen(ids?: string[]): Promise<IMarkReadResponse> {
    return RefabricProURL.patch("notifications/seen", ids === undefined ? {} : { ids }, {}) as unknown as Promise<IMarkReadResponse>;
  },

  // No request body. `archived: false` for an already-archived or unauthorized id is NOT an error —
  // callers should treat it as a no-op, not surface a failure.
  async archive(id: string): Promise<IArchiveResponse> {
    return RefabricProURL.patch(`notifications/${encodeURIComponent(id)}/archive`, {}, {}) as unknown as Promise<IArchiveResponse>;
  },

  /**
   * family: E4 bare · errors: default
   *
   * REF-6854 (C4): resolves an `EnvelopeRead` — `ok` with the preferences, `handled` (the interceptor already
   * showed the UI), or `invalid` (anything that is not `{ canvas_comment_email: boolean }`, incl. an empty body).
   * A rejection propagates unchanged; the caller decides what to show.
   */
  async getEmailPreferences(): Promise<EnvelopeRead<INotificationEmailPreferences>> {
    return readBare(await RefabricProURL.get("notifications/email-preferences", {}), isEmailPreferences);
  },

  /** family: E4 bare · errors: default — same outcomes as `getEmailPreferences`; the body echoes what was stored. */
  async setEmailPreferences(data: INotificationEmailPreferences): Promise<EnvelopeRead<INotificationEmailPreferences>> {
    return readBare(await RefabricProURL.put("notifications/email-preferences", data, {}), isEmailPreferences);
  },

  async getActiveJobs(config?: { signal?: AbortSignal }): Promise<IActiveJobsResponse> {
    return RefabricProURL.get("jobs/active", { ...config }) as unknown as Promise<IActiveJobsResponse>;
  },
};
